import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

import {
  getIntegrationStatus,
  disconnectPlatform,
  syncProducts,
} from "../../helpers/api/custom/integrationApi";
import { getWebhookLogs } from "../../helpers/api/custom/webhookLogsApi";

import { IntegrationActionTypes } from "./constants";
import {
  loadIntegrationStatusSuccess,
  loadIntegrationStatusError,
  disconnectPlatformSuccess,
  disconnectPlatformError,
  syncProductsSuccess,
  syncProductsError,
  loadWebhookLogsSuccess,
  loadWebhookLogsError,
} from "./actions";

function* loadIntegrationStatusSaga(action: any): SagaIterator {
  try {
    const { sellerId } = action.payload;
    const response = yield call(getIntegrationStatus, sellerId);
    const data = response.data?.data || response.data;
    yield put(loadIntegrationStatusSuccess(data));
  } catch (error: any) {
    const msg = error?.response?.data?.error || error?.message || "Failed to load";
    yield put(loadIntegrationStatusError(msg));
  }
}

function* disconnectPlatformSaga(action: any): SagaIterator {
  try {
    const { platform, sellerId, integrationId } = action.payload;
    yield call(disconnectPlatform, platform, { sellerId, integrationId });
    yield put(disconnectPlatformSuccess());
    yield put({ type: IntegrationActionTypes.LOAD_INTEGRATION_STATUS, payload: { sellerId } });
  } catch (error: any) {
    const msg = error?.response?.data?.error || error?.message || "Disconnect failed";
    yield put(disconnectPlatformError(msg));
  }
}

function* syncProductsSaga(action: any): SagaIterator {
  try {
    const { platform, sellerId, integrationId } = action.payload;
    const response = yield call(syncProducts, platform, { sellerId, integrationId });
    const data = response.data || response;
    yield put(syncProductsSuccess(platform, data?.count));
    yield put({ type: IntegrationActionTypes.LOAD_INTEGRATION_STATUS, payload: { sellerId } });
  } catch (error: any) {
    const msg = error?.response?.data?.error || error?.message || "Sync failed";
    yield put(syncProductsError(msg));
  }
}

function* loadWebhookLogsSaga(action: any): SagaIterator {
  try {
    const params = action.payload;
    const response = yield call(getWebhookLogs, params);
    const data = response.data || response;
    yield put(loadWebhookLogsSuccess(data.items || [], data.total || 0));
  } catch (error: any) {
    // A request that never reached the server (CORS / offline / DNS) has no
    // `response` and axios reports a bare "Network Error" — surface something
    // actionable instead of that opaque string.
    const msg = error?.response?.data?.error
      || (error?.response ? error?.message : "Cannot reach the API — check your connection or that this domain is allowed (CORS).")
      || "Failed to load";
    yield put(loadWebhookLogsError(msg));
  }
}

export function* watchLoadIntegrationStatus() {
  yield takeEvery(IntegrationActionTypes.LOAD_INTEGRATION_STATUS, loadIntegrationStatusSaga);
}

export function* watchDisconnectPlatform() {
  yield takeEvery(IntegrationActionTypes.DISCONNECT_PLATFORM, disconnectPlatformSaga);
}

export function* watchSyncProducts() {
  yield takeEvery(IntegrationActionTypes.SYNC_PRODUCTS, syncProductsSaga);
}

export function* watchLoadWebhookLogs() {
  yield takeEvery(IntegrationActionTypes.LOAD_WEBHOOK_LOGS, loadWebhookLogsSaga);
}

function* integrationSaga() {
  yield all([
    fork(watchLoadIntegrationStatus),
    fork(watchDisconnectPlatform),
    fork(watchSyncProducts),
    fork(watchLoadWebhookLogs),
  ]);
}

export default integrationSaga;
